import { OrderSuggestionSheet } from "@/components/operations";
import { SuccessActionPanel } from "@/components/operations/SuccessActionPanel";
import type { CandidateStatus, OrderCandidate, QueryConstraint } from "@/core";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useApproveOrderCandidate,
  useCustomerMapByIds,
  useOrderCandidates,
  useRejectOrderCandidate,
} from "@/hooks";
import { useSourceDeals } from "@/hooks/service-hooks/deals/use-won-deals";
import {
  buildInventoryWorkspacePath,
  formatStatusLabel,
  getSuggestionAttentionDetails,
} from "@/components/operations/operations-shared";
import { HealthBadge } from "@/components/operations/operations-view-utils";
import { useProductsStore } from "@/stores/products";
import { formatTimestamp, timestampToDate } from "@/utils/formatting";
import { getHubspotDealHref } from "@/utils/get-hubspot-deal-href";
import { format } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarIcon,
  Filter,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

interface SuccessState {
  title: string;
  description: string;
  details?: string[];
  nextAction?: string;
  actions?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
}

type TypeFilter = "all" | "po" | "so";
type StatusFilter = "open" | "all" | CandidateStatus;
type SortDirection = "asc" | "desc";
type SortKey =
  | "candidateType"
  | "status"
  | "attention"
  | "productName"
  | "customer"
  | "quantity"
  | "amount"
  | "sourceStageUpdatedAt";

interface SortState {
  key: SortKey;
  direction: SortDirection;
}

interface OrderSuggestionFilters {
  searchTerm: string;
  type: TypeFilter;
  productId: string;
  status: StatusFilter;
  createdDateRange: DateRange | undefined;
}

const DEFAULT_FILTERS: OrderSuggestionFilters = {
  searchTerm: "",
  type: "all",
  productId: "all",
  status: "open",
  createdDateRange: undefined,
};

function SortableHeader(payload: {
  label: string;
  sortKey: SortKey;
  sortState: SortState;
  onSort: (key: SortKey) => void;
}) {
  const { label, sortKey, sortState, onSort } = payload;
  const isActive = sortState.key === sortKey;

  return (
    <Button
      variant="ghost"
      onClick={() => onSort(sortKey)}
      className="h-auto p-0 font-semibold hover:bg-transparent"
    >
      {label}
      {isActive ? (
        sortState.direction === "asc" ? (
          <ArrowUp className="ml-2 h-4 w-4" />
        ) : (
          <ArrowDown className="ml-2 h-4 w-4" />
        )
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

export default function OrderSuggestionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCandidateId = searchParams.get("candidateId") || "";
  const { products } = useProductsStore();
  const [filters, setFilters] = useState<OrderSuggestionFilters>(DEFAULT_FILTERS);
  const dateFilterActive = Boolean(filters.createdDateRange?.from || filters.createdDateRange?.to);
  const searchActive = filters.searchTerm.trim().length > 0;
  const pageSize = dateFilterActive || searchActive ? 10_000 : 25;
  const orderCandidateQueryConstraints = useMemo(() => {
    const nextConstraints: QueryConstraint<keyof OrderCandidate & string>[] = [
      { field: "quantity", operator: ">", value: 0 },
    ];

    if (filters.status === "open") {
      nextConstraints.push({
        field: "status",
        operator: "in",
        value: ["suggested", "approved"],
      });
    }

    if (filters.status !== "open" && filters.status !== "all") {
      nextConstraints.push({
        field: "status",
        operator: "==",
        value: filters.status,
      });
    }

    if (filters.type !== "all") {
      nextConstraints.push({
        field: "candidateType",
        operator: "==",
        value: filters.type,
      });
    }

    if (filters.productId !== "all") {
      nextConstraints.push({
        field: "productId",
        operator: "==",
        value: filters.productId,
      });
    }

    if (filters.createdDateRange?.from) {
      const startDate = new Date(filters.createdDateRange.from);
      startDate.setHours(0, 0, 0, 0);

      nextConstraints.push({
        field: "sourceStageUpdatedAt",
        operator: ">=",
        value: startDate,
      });
    }

    if (filters.createdDateRange?.from || filters.createdDateRange?.to) {
      const endDate = filters.createdDateRange?.to || filters.createdDateRange?.from;

      if (endDate) {
        const normalizedEndDate = new Date(endDate);
        normalizedEndDate.setHours(23, 59, 59, 999);

        nextConstraints.push({
          field: "sourceStageUpdatedAt",
          operator: "<=",
          value: normalizedEndDate,
        });
      }
    }

    return nextConstraints;
  }, [filters.createdDateRange, filters.productId, filters.status, filters.type]);
  const orderCandidatesOrderBy = useMemo(
    () =>
      filters.createdDateRange?.from || filters.createdDateRange?.to
        ? { field: "sourceStageUpdatedAt", direction: "desc" as const }
        : { field: "sourceStageUpdatedAt", direction: "desc" as const },
    [filters.createdDateRange?.from, filters.createdDateRange?.to],
  );
  const orderCandidatesQuery = useOrderCandidates({
    pagination: { limit: pageSize },
    queryConstraints: orderCandidateQueryConstraints,
    orderBy: orderCandidatesOrderBy,
  });

  useEffect(() => {
    if (orderCandidatesQuery.error) {
      console.error("[order-suggestions] query error:", orderCandidatesQuery.error);
    }
  }, [orderCandidatesQuery.error]);

  useEffect(() => {
    setDisplayLimit(25);
  }, [filters.createdDateRange]);

  const approveMutation = useApproveOrderCandidate();
  const rejectMutation = useRejectOrderCandidate();
  const [successState, setSuccessState] = useState<SuccessState | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [displayLimit, setDisplayLimit] = useState(25);
  const [sortState, setSortState] = useState<SortState>({
    key: "sourceStageUpdatedAt",  
    direction: "desc",
  });
  const allCandidates = useMemo(
    () => orderCandidatesQuery.data?.pages.flat() ?? [],
    [orderCandidatesQuery.data],
  );
  const sourceDealIds = useMemo(
    () =>
      Array.from(
        new Set(
          allCandidates
            .map((candidate) => candidate.sourceDealId)
            .filter(
              (dealId): dealId is string =>
                typeof dealId === "string" && dealId.trim().length > 0,
            ),
        ),
      ),
    [allCandidates],
  );
  const sourceDealsQuery = useSourceDeals(sourceDealIds);
  const sourceDealMap = useMemo(
    () => new Map((sourceDealsQuery.data ?? []).map((deal) => [deal.id, deal])),
    [sourceDealsQuery.data],
  );
  const customerIds = useMemo(
    () => Array.from(new Set(allCandidates.map((candidate) => candidate.customerId))),
    [allCandidates],
  );
  const { customerMap } = useCustomerMapByIds(customerIds);
  const productOptions = useMemo(
    () =>
      (products.length > 0
        ? products.map((product) => ({ id: product.id, name: product.name }))
        : Array.from(
            new Map(
              allCandidates.map((candidate) => [candidate.productId, candidate.productName]),
            ).entries(),
          ).map(([id, name]) => ({ id, name })))
        .sort((left, right) => left.name.localeCompare(right.name)),
    [allCandidates, products],
  );
  const candidates = allCandidates;

  const resolveCustomerLabel = useCallback(
    (candidate: (typeof candidates)[number]) => {
      return (
        candidate.customerDisplayName ||
        customerMap.get(candidate.customerId)?.displayName ||
        candidate.customerEmail ||
        customerMap.get(candidate.customerId)?.email ||
        "Unknown customer"
      );
    },
    [customerMap],
  );

  const updateFilter = <K extends keyof OrderSuggestionFilters>(
    key: K,
    value: OrderSuggestionFilters[K],
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const filteredCandidates = useMemo(() => {
    const normalizedSearchTerms = filters.searchTerm
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    return candidates.filter((candidate) => {
      if (normalizedSearchTerms.length === 0) {
        return true;
      }

      const sourceDeal = sourceDealMap.get(candidate.sourceDealId);
      const resolvedCustomerLabel = resolveCustomerLabel(candidate);
      const searchValues = [
        candidate.id,
        candidate.sourceDealId,
        sourceDeal?.name,
        candidate.productName,
        candidate.customerDisplayName,
        resolvedCustomerLabel,
        candidate.customerEmail,
        customerMap.get(candidate.customerId)?.email,
      ]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.toLowerCase());

      return normalizedSearchTerms.every((searchTerm) =>
        searchValues.some((value) => value.includes(searchTerm)),
      );
    });
  }, [candidates, customerMap, filters.searchTerm, resolveCustomerLabel, sourceDealMap]);

  const handleSort = (key: SortKey) => {
    setSortState((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: key === "sourceStageUpdatedAt" ? "desc" : "asc",
      };
    });
  };

  const sortedCandidates = useMemo(() => {
    const attentionRank: Record<string, number> = {
      Blocked: 0,
      "Needs Action": 1,
      "At Risk": 2,
      Ready: 3,
      Complete: 4,
    };

    const getAttentionValue = (candidate: (typeof candidates)[number]) => {
      const details = getSuggestionAttentionDetails({
        status: candidate.status,
        candidateType: candidate.candidateType,
      });

      return attentionRank[details.state] ?? 999;
    };

    const directionMultiplier = sortState.direction === "asc" ? 1 : -1;
    const nextCandidates = [...filteredCandidates];

    nextCandidates.sort((left, right) => {
      const compareStrings = (leftValue: string, rightValue: string) =>
        leftValue.localeCompare(rightValue) * directionMultiplier;
      const compareNumbers = (leftValue: number, rightValue: number) =>
        (leftValue - rightValue) * directionMultiplier;

      if (sortState.key === "candidateType") {
        return compareStrings(left.candidateType, right.candidateType);
      }

      if (sortState.key === "status") {
        return compareStrings(left.status, right.status);
      }

      if (sortState.key === "attention") {
        return compareNumbers(getAttentionValue(left), getAttentionValue(right));
      }

      if (sortState.key === "productName") {
        return compareStrings(left.productName, right.productName);
      }

      if (sortState.key === "customer") {
        return compareStrings(resolveCustomerLabel(left), resolveCustomerLabel(right));
      }

      if (sortState.key === "quantity") {
        return compareNumbers(left.quantity, right.quantity);
      }

      if (sortState.key === "amount") {
        return compareNumbers(left.amount, right.amount);
      }

      return compareNumbers(
        timestampToDate(left.sourceStageUpdatedAt)?.getTime() ?? 0,
        timestampToDate(right.sourceStageUpdatedAt)?.getTime() ?? 0,
      );
    });

    return nextCandidates;
  }, [filteredCandidates, sortState, resolveCustomerLabel]);

  const displayedCandidates = dateFilterActive
    ? sortedCandidates.slice(0, displayLimit)
    : sortedCandidates;

  const handleApprove = async (candidateId: string) => {
    const candidate = sortedCandidates.find((item) => item.id === candidateId);
    let result: Awaited<ReturnType<typeof approveMutation.mutateAsync>>;
    try {
      result = await approveMutation.mutateAsync({
        candidateId,
      });
    } catch (error) {
      setSuccessState(null);
      setActionError(
        error instanceof Error && error.message
          ? error.message
          : "Approval failed. Open the suggestion to review the required fields and try again.",
      );
      return;
    }

    setActionError(null);

    if (!candidate) {
      return;
    }

    const sourceDeal = sourceDealMap.get(candidate.sourceDealId);
    const crmHref = getHubspotDealHref({
      dealId: candidate.sourceDealId,
      hubspotLink: sourceDeal?.hubspotLink,
    });
    const sourceDealActions: NonNullable<SuccessState["actions"]> = [];

    if (candidate.sourceDealId) {
      sourceDealActions.push({
        label: "View Source Deal",
        href: `/deals?dealId=${candidate.sourceDealId}`,
      });
    }

    if (crmHref) {
      sourceDealActions.push({
        label: "Open in CRM",
        onClick: () => {
          window.open(crmHref, "_blank", "noopener,noreferrer");
        },
      });
    }

    setSuccessState({
      title:
        result.candidateType === "po" ? "Purchase Order created" : "Sales Order created",
      description:
        result.candidateType === "po"
          ? "Approval created the purchase order and its linked inventory records."
          : "Approval created the sales order inside the selected inventory workspace.",
      details:
        result.candidateType === "po"
          ? [
              "Inventory was created or linked.",
              "Inventory credentials were initialized.",
            ]
          : [
              "Inventory totals were updated.",
              "The sales order was linked to the resolved inventory.",
            ],
      nextAction:
        result.candidateType === "po"
          ? "Review payment readiness or complete account details."
          : "Review sell-side financials and create an invoice when needed.",
      actions:
        result.candidateType === "po"
          ? [
              { label: "Open PO", href: `/operations/purchase-orders?poId=${result.convertedOrderId}` },
              result.inventoryId
                ? {
                    label: "Open Inventory Workspace",
                    href: buildInventoryWorkspacePath({
                      inventoryId: result.inventoryId,
                      purchaseOrderId: result.convertedOrderId,
                      focus: "purchase-orders",
                    }),
                  }
                : null,
              ...sourceDealActions,
            ].filter(Boolean) as SuccessState["actions"]
          : [
              { label: "Open SO", href: `/operations/sales-orders?soId=${result.convertedOrderId}` },
              result.inventoryId
                ? {
                    label: "Open Inventory Workspace",
                    href: buildInventoryWorkspacePath({
                      inventoryId: result.inventoryId,
                      salesOrderId: result.convertedOrderId,
                      focus: "sell-orders",
                    }),
                  }
                : null,
              ...sourceDealActions,
            ].filter(Boolean) as SuccessState["actions"],
    });
  };

  const handleReject = async (candidateId: string) => {
    try {
      await rejectMutation.mutateAsync({
        candidateId,
        reason: "Rejected by operations",
      });
      setActionError(null);
    } catch (error) {
      setActionError(
        error instanceof Error && error.message
          ? error.message
          : "Rejecting the suggestion failed. Please try again.",
      );
    }
  };

  const handleOpenCandidate = (candidateId: string) => {
    setSearchParams({ candidateId }, { replace: true });
  };

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) {
      setSearchParams({}, { replace: true });
    }
  };

  const handleLoadMore = () => {
    if (!orderCandidatesQuery.hasNextPage || orderCandidatesQuery.isFetchingNextPage) {
      return;
    }

    orderCandidatesQuery.fetchNextPage();
  };

  const hasActiveFilters =
    filters.type !== DEFAULT_FILTERS.type ||
    filters.productId !== DEFAULT_FILTERS.productId ||
    filters.status !== DEFAULT_FILTERS.status ||
    Boolean(filters.searchTerm.trim()) ||
    Boolean(filters.createdDateRange?.from || filters.createdDateRange?.to);
  const activeFilterCount = [
    filters.type !== DEFAULT_FILTERS.type ? filters.type : null,
    filters.productId !== DEFAULT_FILTERS.productId ? filters.productId : null,
    filters.status !== DEFAULT_FILTERS.status ? filters.status : null,
    filters.searchTerm.trim() ? filters.searchTerm : null,
    filters.createdDateRange?.from || filters.createdDateRange?.to
      ? filters.createdDateRange
      : null,
  ].filter(Boolean).length;
  const selectedProductLabel =
    productOptions.find((product) => product.id === filters.productId)?.name ||
    filters.productId;

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {successState ? (
        <SuccessActionPanel
          title={successState.title}
          description={successState.description}
          details={successState.details}
          nextAction={successState.nextAction}
          actions={successState.actions}
          onDismiss={() => setSuccessState(null)}
        />
      ) : null}

      {actionError ? (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div>
            <div className="font-medium">Unable to approve suggestion</div>
            <div className="mt-1">{actionError}</div>
          </div>
          <button
            type="button"
            className="text-xs underline underline-offset-2"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Order Suggestions
            {activeFilterCount > 0 ? (
              <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium">
                {activeFilterCount}
              </span>
            ) : null}
          </CardTitle>
          <CardDescription>
            Suggested purchase and sales orders generated from closed-won deals.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm text-muted-foreground">
                {sortedCandidates.length} suggestion{sortedCandidates.length === 1 ? "" : "s"}
              </div>
              {hasActiveFilters ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              ) : null}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1.35fr)_minmax(12rem,1fr)_minmax(12rem,1fr)_minmax(10rem,1fr)] [&>*]:min-w-0">
              <div className="space-y-2">
                <Label htmlFor="order-suggestion-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="order-suggestion-search"
                    className="pl-9"
                    placeholder="Search deal, product, customer, email, or ID..."
                    value={filters.searchTerm}
                    onChange={(event) => updateFilter("searchTerm", event.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Converted Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full min-w-0 justify-start overflow-hidden text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">
                        {filters.createdDateRange?.from ? (
                          filters.createdDateRange.to ? (
                            `${format(filters.createdDateRange.from, "yyyy-MM-dd")} - ${format(filters.createdDateRange.to, "yyyy-MM-dd")}`
                          ) : (
                            format(filters.createdDateRange.from, "yyyy-MM-dd")
                          )
                        ) : (
                          "Converted date range"
                        )}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={filters.createdDateRange}
                      onSelect={(range) => updateFilter("createdDateRange", range)}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Product</Label>
                <Select
                  value={filters.productId}
                  onValueChange={(value) => updateFilter("productId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {productOptions.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => updateFilter("status", value as StatusFilter)}
                >
                  <SelectTrigger>
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open only</SelectItem>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="suggested">Suggested</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="invalidated">Invalidated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={filters.type}
                  onValueChange={(value) => updateFilter("type", value as TypeFilter)}
                >
                  <SelectTrigger>
                    <Filter className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="po">PO</SelectItem>
                    <SelectItem value="so">SO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasActiveFilters ? (
              <div className="flex flex-wrap gap-2">
                {filters.searchTerm.trim() ? (
                  <Badge variant="secondary" className="gap-1">
                    Search: {filters.searchTerm.trim()}
                  </Badge>
                ) : null}
                {filters.type !== DEFAULT_FILTERS.type ? (
                  <Badge variant="secondary" className="gap-1">
                    Type: {formatStatusLabel(filters.type)}
                  </Badge>
                ) : null}
                {filters.status !== DEFAULT_FILTERS.status ? (
                  <Badge variant="secondary" className="gap-1">
                    Status: {filters.status === "all" ? "All" : formatStatusLabel(filters.status)}
                  </Badge>
                ) : null}
                {filters.productId !== DEFAULT_FILTERS.productId ? (
                  <Badge variant="secondary" className="gap-1">
                    Product: {selectedProductLabel}
                  </Badge>
                ) : null}
                {filters.createdDateRange?.from ? (
                  <Badge variant="secondary" className="gap-1">
                    Date:{" "}
                    {filters.createdDateRange.to
                      ? `${format(filters.createdDateRange.from, "MMM dd")} - ${format(filters.createdDateRange.to, "MMM dd")}`
                      : format(filters.createdDateRange.from, "MMM dd")}
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader
                    label="Type"
                    sortKey="candidateType"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Attention"
                    sortKey="attention"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Product"
                    sortKey="productName"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Customer"
                    sortKey="customer"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Quantity"
                    sortKey="quantity"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Amount"
                    sortKey="amount"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Converted"
                    sortKey="sourceStageUpdatedAt"
                    sortState={sortState}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orderCandidatesQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    Loading suggestions...
                  </TableCell>
                </TableRow>
              ) : sortedCandidates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    No pending suggestions.
                  </TableCell>
                </TableRow>
              ) : (
                displayedCandidates.map((candidate) => {
                  const attention = getSuggestionAttentionDetails({
                    status: candidate.status,
                    candidateType: candidate.candidateType,
                  });

                  return (
                    <TableRow
                      key={candidate.id}
                      className="cursor-pointer"
                      onClick={() => handleOpenCandidate(candidate.id)}
                    >
                    <TableCell>
                      <Badge variant="outline">
                        {formatStatusLabel(candidate.candidateType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge>{formatStatusLabel(candidate.status)}</Badge>
                    </TableCell>
                    <TableCell>
                      <HealthBadge state={attention.state} reason={attention.reason} />
                    </TableCell>
                    <TableCell>{candidate.productName}</TableCell>
                    <TableCell>{resolveCustomerLabel(candidate)}</TableCell>
                    <TableCell>{candidate.quantity.toLocaleString()}</TableCell>
                    <TableCell>${candidate.amount.toLocaleString()}</TableCell>
                    <TableCell>
                      {formatTimestamp(
                        candidate.sourceStageUpdatedAt,
                        "yyyy-MM-dd HH:mm",
                      ) || "-"}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {candidate.convertedOrderId ? (
                        <Button
                          asChild
                          size="sm"
                          variant="ghost"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Link
                            to={
                              candidate.candidateType === "po"
                                ? `/operations/purchase-orders?poId=${candidate.convertedOrderId}`
                                : `/operations/sales-orders?soId=${candidate.convertedOrderId}`
                            }
                          >
                            {candidate.candidateType === "po" ? "Open PO" : "Open SO"}
                          </Link>
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleApprove(candidate.id);
                        }}
                        disabled={approveMutation.isPending}
                        loading={
                          approveMutation.isPending &&
                          approveMutation.variables?.candidateId === candidate.id
                        }
                        loadingText="Approving..."
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleReject(candidate.id);
                        }}
                        disabled={rejectMutation.isPending}
                        loading={
                          rejectMutation.isPending &&
                          rejectMutation.variables?.candidateId === candidate.id
                        }
                        loadingText="Rejecting..."
                      >
                        Reject
                      </Button>
                    </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {dateFilterActive ? (
            displayLimit < sortedCandidates.length ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setDisplayLimit((limit) => limit + 25)}
                >
                  Load More
                </Button>
              </div>
            ) : null
          ) : orderCandidatesQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={orderCandidatesQuery.isFetchingNextPage}
              >
                {orderCandidatesQuery.isFetchingNextPage ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <OrderSuggestionSheet
        candidateId={selectedCandidateId}
        open={selectedCandidateId.length > 0}
        onOpenChange={handleSheetOpenChange}
      />
    </div>
  );
}
